import React, { useState, useEffect } from 'react';
import { Calendar, Utensils, ShoppingBag, Compass, Tag, TrendingUp, Ticket, CheckCircle2, Headphones, Sparkles, Star, Zap, MapPin, Camera, Gift, Coffee, ShieldCheck } from 'lucide-react';

const categorySlidesData = {
  restaurant: [
    {
      id: 1,
      tag: 'Fine Dining & Table Desk',
      title: (
        <>
          Streamline Table Reservations. <span className="italic text-[#E96726]">Gourmet Dining.</span> Exceptional Hospitality.
        </>
      ),
      subtitle: 'Manage live restaurant seating, accept instant table reservations, and delight hungry diners.',
      badges: [
        { icon: Calendar, text: 'Rooftop Seating Manager' },
        { icon: Utensils, text: 'Instant Table Confirmations' },
        { icon: TrendingUp, text: 'High Diner Loyalty' }
      ],
      cardTag: 'Fine Dining Desk',
      cardHeading: 'Table Availability Console',
      cardDetail: 'Live Seating Capacity & Reservations',
      bgImage: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 2,
      tag: 'Culinary Voucher Packages',
      title: (
        <>
          Chef’s Special Vouchers. <span className="italic text-[#E96726]">Exclusive Dining Packages.</span> Boost Food Sales.
        </>
      ),
      subtitle: 'Offer pre-booked dining packages, promotional meal vouchers, and track food revenue in real-time.',
      badges: [
        { icon: Ticket, text: 'Gourmet Meal Packages' },
        { icon: Zap, text: 'Promotional Dining Vouchers' },
        { icon: Star, text: 'Food Revenue Tracker' }
      ],
      cardTag: 'Chef Specials',
      cardHeading: 'Culinary Voucher Manager',
      cardDetail: 'Pre-Booked Meal Offers & Digital Menu',
      bgImage: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 3,
      tag: 'Bistro & Cafe Influx',
      title: (
        <>
          Bistro & Brunch Bookings. <span className="italic text-[#E96726]">Zero Queue Waiting.</span> Happy Foodies.
        </>
      ),
      subtitle: 'Eliminate queue wait times, manage peak hour dining capacity, and capture 5-star foodie reviews.',
      badges: [
        { icon: Coffee, text: 'Peak Hour Seating Sync' },
        { icon: ShieldCheck, text: 'Zero Queue Waiting Time' },
        { icon: Star, text: '5-Star Foodie Reviews' }
      ],
      cardTag: 'Bistro Console',
      cardHeading: 'Cafe & Bistro Booking Desk',
      cardDetail: 'Peak Hours Capacity & Guest Influx',
      bgImage: 'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?auto=format&fit=crop&w=600&q=80'
    }
  ],
  shop: [
    {
      id: 1,
      tag: 'Boutique & Retail Desk',
      title: (
        <>
          Boost In-Store Shopping. <span className="italic text-[#E96726]">Exclusive Retail Deals.</span> High Footfall.
        </>
      ),
      subtitle: 'Issue store shopping vouchers, attract tourists to your boutique, and drive retail customer footfall.',
      badges: [
        { icon: ShoppingBag, text: 'Fashion & Boutique Vouchers' },
        { icon: Tag, text: 'In-Store Tourist Discounts' },
        { icon: TrendingUp, text: 'High Retail Footfall' }
      ],
      cardTag: 'Boutique Console',
      cardHeading: 'Retail Footfall Manager',
      cardDetail: 'In-Store Shopper Influx & Checkout',
      bgImage: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 2,
      tag: 'Souvenir & Gift Desk',
      title: (
        <>
          Souvenir & Handicraft Vouchers. <span className="italic text-[#E96726]">Instant Checkout.</span> Higher Basket Value.
        </>
      ),
      subtitle: 'Promote local souvenir packages, redeem tourist discount vouchers, and speed up store checkout.',
      badges: [
        { icon: Gift, text: 'Souvenir Discount Vouchers' },
        { icon: Zap, text: 'Express Store Checkout' },
        { icon: TrendingUp, text: 'Higher Sales Basket Value' }
      ],
      cardTag: 'Gift Shop Desk',
      cardHeading: 'Souvenir Voucher Redemption',
      cardDetail: 'Instant QR Tourist Voucher Checkout',
      bgImage: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 3,
      tag: 'Luxury Brand Retail',
      title: (
        <>
          Luxury Shopping Passes. <span className="italic text-[#E96726]">Smart Store Redemption.</span> Repeat Sales.
        </>
      ),
      subtitle: 'Track daily retail voucher redemptions, analyze top-selling items, and build loyal repeat customers.',
      badges: [
        { icon: Tag, text: 'Luxury Shopping Passes' },
        { icon: CheckCircle2, text: 'Real-time Sales Analytics' },
        { icon: Star, text: 'Repeat Retail Shoppers' }
      ],
      cardTag: 'Retail Analytics',
      cardHeading: 'Store Sales Console',
      cardDetail: 'Daily Shopping Voucher Redemptions',
      bgImage: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=600&q=80'
    }
  ],
  activity: [
    {
      id: 1,
      tag: 'Outdoor & Marine Adventures',
      title: (
        <>
          Supercharge Outdoor Activities. <span className="italic text-[#E96726]">Thrilling Experiences.</span> Infinite Memories.
        </>
      ),
      subtitle: 'Publish adventure packages, manage scuba & water activity slots, and issue instant activity vouchers.',
      badges: [
        { icon: Compass, text: 'Adventure Slot Manager' },
        { icon: Ticket, text: 'Instant Activity Vouchers' },
        { icon: TrendingUp, text: '100% Guest Satisfaction' }
      ],
      cardTag: 'Marine Adventure',
      cardHeading: 'Scuba & Water Sports Desk',
      cardDetail: 'Live Slot Booking & Traveler Check-in',
      bgImage: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 2,
      tag: 'Trekking & Sky Tours',
      title: (
        <>
          Seamless Ticket Desk. <span className="italic text-[#E96726]">Instant QR Check-ins.</span> Zero Tour Delays.
        </>
      ),
      subtitle: 'Verify guest activity tickets on-site, manage tour guide rosters, and track slot influx in real-time.',
      badges: [
        { icon: Zap, text: 'On-Site Ticket Scanner' },
        { icon: Camera, text: 'Group Tour Scheduling' },
        { icon: CheckCircle2, text: 'Real-time Slot Sync' }
      ],
      cardTag: 'Sky & Trek Desk',
      cardHeading: 'Trekking & Extreme Sports',
      cardDetail: 'Live QR Ticket Validation & Roster',
      bgImage: 'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 3,
      tag: 'Water Parks & Theme Safaris',
      title: (
        <>
          Water Park & Safari Passes. <span className="italic text-[#E96726]">Instant Family Passes.</span> Glowing Reviews.
        </>
      ),
      subtitle: 'Issue family activity vouchers, track daily ticket redemptions, and collect 5-star traveler reviews.',
      badges: [
        { icon: MapPin, text: 'Family Activity Passes' },
        { icon: Ticket, text: 'Daily Ticket Tracking' },
        { icon: Star, text: '5-Star Traveler Reviews' }
      ],
      cardTag: 'Theme Park Desk',
      cardHeading: 'Water Park & Safari Desk',
      cardDetail: 'Family Ticket Redemption & Influx',
      bgImage: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80'
    }
  ]
};

const PartnerDashboardHero = ({ category = 'restaurant' }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const catKey = (category || 'restaurant').toLowerCase();
  const slides = categorySlidesData[catKey] || categorySlidesData.restaurant;

  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused, slides.length]);

  return (
    <div
      className="relative w-full rounded-2xl sm:rounded-3xl overflow-hidden bg-white border border-slate-100 shadow-sm mb-4 sm:mb-6 transition-all duration-500 select-none min-h-[260px] sm:min-h-[300px]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {slides.map((slide, index) => {
        const isActive = index === currentSlide;
        return (
          <div
            key={slide.id}
            className={`w-full h-full p-4 sm:p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8 transition-all duration-700 ease-in-out ${
              isActive ? 'block opacity-100 relative z-10' : 'hidden opacity-0 absolute inset-0 z-0 pointer-events-none'
            }`}
          >
            {/* Background Decorative Graphic Accents */}
            <div className="absolute top-0 right-1/3 w-64 sm:w-96 h-64 sm:h-96 bg-teal-50/40 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
            <div className="absolute bottom-0 left-1/4 w-48 sm:w-80 h-48 sm:h-80 bg-orange-50/40 rounded-full blur-3xl translate-y-1/2 pointer-events-none"></div>

            {/* Left side: Accent Pill Bar + Content */}
            <div className="flex items-center gap-3 sm:gap-5 flex-1 relative z-10 w-full">
              {/* Far Left Vertical Gradient Pill Bar */}
              <div className="w-2 sm:w-2.5 h-28 sm:h-40 rounded-full bg-gradient-to-b from-[#067978] via-teal-500 to-[#E96726] shrink-0 hidden sm:block shadow-sm"></div>

              <div className="space-y-2.5 sm:space-y-4 flex-1">
                {/* Top Category Tag Badge */}
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-widest bg-teal-50 text-[#067978] border border-teal-100">
                  <Sparkles size={11} className="text-[#067978]" />
                  {slide.tag}
                </div>

                {/* Main Typography Headline */}
                <h2 className="text-xl sm:text-3xl lg:text-4xl font-black text-[#067978] tracking-tight leading-tight">
                  {slide.title}
                </h2>

                <p className="text-slate-500 text-xs sm:text-sm font-medium max-w-xl hidden sm:block leading-relaxed">
                  {slide.subtitle}
                </p>

                {/* Bottom Orange Pill Badge Bar */}
                <div className="inline-flex flex-wrap items-center gap-2 sm:gap-3 bg-[#E96726] px-3.5 sm:px-6 py-2 sm:py-2.5 rounded-xl sm:rounded-full text-white shadow-md shadow-orange-500/20 max-w-full">
                  {slide.badges.map((badge, idx) => {
                    const Icon = badge.icon;
                    return (
                      <div key={idx} className="flex items-center gap-1.5 sm:gap-2 text-[9px] sm:text-xs font-extrabold whitespace-nowrap">
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white text-[#E96726] flex items-center justify-center shrink-0 shadow-sm">
                          <Icon size={12} />
                        </div>
                        <span>{badge.text}</span>
                        {idx < slide.badges.length - 1 && (
                          <span className="hidden md:inline text-white/40 ml-1.5">•</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Side: Fluid Category Graphic Showcase Card */}
            <div className="relative z-10 w-full lg:w-[340px] xl:w-[380px] shrink-0">
              {/* Decorative Ring Nodes */}
              <div className="absolute -top-2.5 -left-2.5 sm:-top-3 sm:-left-3 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#067978] border-2 border-white shadow-md z-20"></div>
              <div className="absolute -bottom-2.5 -right-2.5 sm:-bottom-3 sm:-right-3 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#E96726] border-2 border-white shadow-md z-20"></div>

              <div className="w-full bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 shadow-lg border border-slate-100 space-y-3 sm:space-y-4 hover:shadow-xl transition-all duration-300 relative overflow-hidden group">
                
                {/* Card Image Banner Container */}
                <div className="relative h-28 sm:h-32 rounded-xl sm:rounded-2xl overflow-hidden bg-slate-100">
                  <img
                    src={slide.bgImage}
                    alt={slide.cardHeading}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent"></div>
                  <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 px-2 sm:px-2.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-widest bg-white/20 backdrop-blur-md text-white border border-white/30">
                    {slide.cardTag}
                  </div>
                  <div className="absolute bottom-2.5 left-2.5 right-2.5 sm:bottom-3 sm:left-3 sm:right-3 text-white">
                    <p className="text-xs font-black truncate">{slide.cardHeading}</p>
                    <p className="text-[10px] text-white/80 font-medium truncate">{slide.cardDetail}</p>
                  </div>
                </div>

                {/* Status Row */}
                <div className="bg-slate-50/80 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-2.5">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-[#067978] text-white flex items-center justify-center font-bold shadow-sm shrink-0">
                      <CheckCircle2 size={15} />
                    </div>
                    <div>
                      <p className="text-[11px] sm:text-xs font-black text-slate-800">Trrip Smart System</p>
                      <p className="text-[9px] sm:text-[10px] text-emerald-600 font-bold">100% Active & Syncing</p>
                    </div>
                  </div>
                  <span className="text-[9px] sm:text-[10px] font-mono font-black text-[#067978] bg-teal-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg sm:rounded-xl border border-teal-100">
                    Verified
                  </span>
                </div>

              </div>
            </div>

          </div>
        );
      })}

      {/* Slider Pagination Controls (Dots) */}
      <div className="absolute bottom-3 right-4 sm:bottom-4 sm:right-8 flex gap-1.5 z-30">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'w-5 sm:w-6 bg-[#067978]' : 'w-2 bg-slate-200 hover:bg-slate-300'
            }`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>

    </div>
  );
};

export default PartnerDashboardHero;
